if (!customElements.get('grouped-variant-picker')) {
  customElements.define('grouped-variant-picker', class GroupedVariantPicker extends HTMLElement {
    constructor() {
      super();
      const mapEl = this.querySelector('script[data-grouped-variants-map]');
      try {
        this.siblingMap = mapEl ? JSON.parse(mapEl.textContent) : { siblings: {} };
      } catch (err) {
        console.error('grouped-variant-picker: failed to parse sibling map', err);
        this.siblingMap = { siblings: {} };
      }
      this.sectionId = this.dataset.section;
      this.productId = this.dataset.productId;
      this.productUrl = this.dataset.productUrl;
      this.addEventListener('change', this.onOptionChange.bind(this));
    }

    getSelectedOptions() {
      const result = {};
      this.querySelectorAll('fieldset.grouped-variant-fieldset').forEach((fs) => {
        const name = fs.dataset.optionName;
        if (!name) return;
        const select = fs.querySelector('select');
        if (select) {
          result[name] = select.value;
          return;
        }
        const checked = fs.querySelector('input[type="radio"]:checked');
        if (checked) result[name] = checked.value;
      });
      return result;
    }

    buildKey(opts) {
      return Object.values(opts).join(' / ');
    }

    onOptionChange(e) {
      const selected = this.getSelectedOptions();
      const key = this.buildKey(selected);
      let sibling = this.siblingMap.siblings[key];

      if (!sibling) {
        const fieldset = e.target.closest('fieldset.grouped-variant-fieldset');
        const changedName = fieldset && fieldset.dataset.optionName;
        const changedValue = e.target.value;
        if (changedName) {
          sibling = this.findClosestSibling(selected, changedName, changedValue);
        }
      }

      if (!sibling) return;
      if (Number(sibling.productId) === Number(this.productId)) return;

      this.loadSibling(sibling);
    }

    findClosestSibling(selected, changedName, changedValue) {
      let best = null;
      let bestScore = -1;
      const siblings = this.siblingMap.siblings;
      for (const key in siblings) {
        const s = siblings[key];
        if (!s.options || s.options[changedName] !== changedValue) continue;
        let score = 0;
        for (const name in selected) {
          if (name === changedName) continue;
          if (s.options[name] === selected[name]) score++;
        }
        if (score > bestScore) {
          bestScore = score;
          best = s;
        }
      }
      return best;
    }

    async loadSibling(sibling) {
      this.classList.add('grouped-variant-picker--loading');
      try {
        const res = await fetch(`${sibling.url}?section_id=${this.sectionId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        const newDoc = new DOMParser().parseFromString(text, 'text/html');

        this.swapById(newDoc, `price-${this.sectionId}`);
        this.swapById(newDoc, `Sku-${this.sectionId}`, true);
        this.swapById(newDoc, `Inventory-${this.sectionId}`);
        this.swapBySelector(newDoc, '.product__title');
        this.swapBySelector(newDoc, '.product__description');
        this.swapBySelector(newDoc, '.product__media-wrapper');
        this.swapElement(newDoc, 'product-form');

        // Swap elements in the main section
        document.querySelectorAll('[data-sibling-swap]').forEach((dest) => {
          if (!dest.id) return;
          const secId = dest.closest('[id^="shopify-section-"]')?.id?.slice('shopify-section-'.length);
          if (secId !== this.sectionId) return;
          const src = newDoc.getElementById(dest.id);
          if (!src) return;
          dest.innerHTML = src.innerHTML;
          dest.style.display = src.style.display;
        });

        // Swap elements in any other sections that also contain data-sibling-swap elements
        const otherSectionIds = new Set();
        document.querySelectorAll('[data-sibling-swap]').forEach((dest) => {
          const secId = dest.closest('[id^="shopify-section-"]')?.id?.slice('shopify-section-'.length);
          if (secId && secId !== this.sectionId) otherSectionIds.add(secId);
        });

        await Promise.all([...otherSectionIds].map(async (secId) => {
          const r = await fetch(`${sibling.url}?section_id=${secId}`);
          if (!r.ok) return;
          const secDoc = new DOMParser().parseFromString(await r.text(), 'text/html');
          document.querySelectorAll('[data-sibling-swap]').forEach((dest) => {
            if (!dest.id) return;
            if (dest.closest('[id^="shopify-section-"]')?.id !== `shopify-section-${secId}`) return;
            const src = secDoc.getElementById(dest.id);
            if (!src) return;
            dest.innerHTML = src.innerHTML;
            dest.style.display = src.style.display;
          });
        }));

        // Re-wrap RTE tables in swapped content so they keep horizontal
        // scroll. The page-load wrapper in scripts.js does not run on
        // innerHTML-swapped sibling content.
        this.wrapRteTables();

        const newPicker = newDoc.querySelector('grouped-variant-picker');
        if (newPicker) {
          this.replaceWith(newPicker);
        }

        window.history.replaceState({}, '', sibling.url);

        document.dispatchEvent(new CustomEvent('shopify:section:load', {
          detail: { sectionId: this.sectionId }
        }));
      } catch (err) {
        console.error('grouped-variant-picker: failed to load sibling', err);
        this.classList.remove('grouped-variant-picker--loading');
      }
    }

    wrapRteTables() {
      document.querySelectorAll('.rte table').forEach((table) => {
        if (table.closest('.table-wrapper')) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'table-wrapper';
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      });
    }

    swapById(newDoc, id, syncVisibility = false) {
      const dest = document.getElementById(id);
      const src = newDoc.getElementById(id);
      if (!dest || !src) return;
      dest.innerHTML = src.innerHTML;
      if (syncVisibility) {
        dest.classList.toggle('visibility-hidden', src.classList.contains('visibility-hidden'));
      }
    }

    swapBySelector(newDoc, selector) {
      const dest = document.querySelector(selector);
      const src = newDoc.querySelector(selector);
      if (!dest || !src) return;
      dest.innerHTML = src.innerHTML;
    }

    swapElement(newDoc, selector) {
      const dest = document.querySelector(selector);
      const src = newDoc.querySelector(selector);
      if (!dest || !src) return;
      dest.replaceWith(src);
    }
  });
}
